/**
 * Firing Phase — multi-projectile update loop, weapon behavior hooks,
 * hit detection, crater/damage/knockback via defaultExplosion(),
 * Target Blast target checking, DI triggering, void retaliation scheduling.
 *
 * Extracted from phases.ts for readability.
 */

import { Time } from 'bonkjs';
import type { InputProvider } from '../input';
import type { GameState, DeferredSpawn } from '../types';
import type { GameEffect } from './effects';
import { transitionPhase } from './effects';
import { updatePostFireMovement } from './movement';
import { updateSingleProjectile } from '../physics/projectile';
import { defaultExplosion, resetSubExplosionTracking } from './explosion';
import { startDIWindow } from './di';
import { TURN_FLOW, VOID_CONSUME, VOID_PERSONALITY, DI, ANNOUNCE, TUMBLE } from '../constants';
import { getActiveTheme } from '../stages/registry';
import { enterTumble } from '../physics/tanks';
import { mirrorEnterTumble, mirrorAirborne } from '../physics/transitions';
import { random, randomChance, randomInt, randomRange } from '../terrain/rng';
import { msToFrames } from './input-edge';
import { modeIsClassic } from './mode';
import { recordTrickHit } from './trick-score';
import { rollConsumeLine, getMoodFontSize } from './void-personality';

/** Check if explosion hits any Target Blast targets. */
function checkTargetHits(state: GameState, x: number, y: number, blastRadius: number, fx: GameEffect[]): void {
  if (state.modeState.mode !== 'target_blast') return;
  const ms = state.modeState;
  for (const target of ms.targets) {
    if (target.hit) continue;
    const dx = x - target.x;
    const dy = y - target.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < blastRadius + target.radius) {
      target.hit = true;
      fx.push({ type: 'target_hit', x: target.x, y: target.y, radius: target.radius });
    }
  }
  if (!ms.allTargetsHit && ms.targets.every(t => t.hit)) {
    ms.allTargetsHit = true;
    ms.exitGateActive = true;
    fx.push({ type: 'exit_gate_activated', x: ms.exitGateX, y: ms.exitGateY });
  }
}

export function handleFiringPhase(state: GameState, input: InputProvider, fx: GameEffect[]): void {
  // Target Blast: timer ticks during firing
  if (state.modeState.mode === 'target_blast' && !state.modeState.runComplete) {
    state.modeState.elapsedTime += Time.fixedDeltaTime;
  }

  // Air control for active player while projectile is in flight
  updatePostFireMovement(state, input);

  // --- Process deferred spawns ---
  for (const ds of state.deferredSpawns) {
    if (!ds.spawned && state.frameCount >= ds.spawnFrame) {
      ds.spawned = true;
      state.projectiles.push(ds.projectile);
    }
  }
  state.deferredSpawns = state.deferredSpawns.filter(ds => !ds.spawned);

  // Camera follows first projectile
  if (state.projectiles.length > 0) {
    const lead = state.projectiles[0];
    fx.push({ type: 'camera_follow', x: lead.x, y: lead.y });
  }

  // --- Iterate projectiles backwards (safe removal) ---
  let shotCounted = false;
  for (let pi = state.projectiles.length - 1; pi >= 0; pi--) {
    const proj = state.projectiles[pi];

    // Call weapon.onTick() if defined
    if (proj.weapon.onTick) {
      const tickResult = proj.weapon.onTick(proj, state, fx);
      if (tickResult === 'detonate') {
        const { knockedPlayers, hitPlayers, damages: tickDmgs } = defaultExplosion(proj, Math.round(proj.x), Math.round(proj.y), state, fx);
        for (let hi = 0; hi < hitPlayers.length; hi++) {
          recordTrickHit(state, proj, hitPlayers[hi], Math.round(proj.x), Math.round(proj.y), tickDmgs[hi] ?? 0, false, fx);
        }
        state.firingPhaseKnockedPlayers.push(...knockedPlayers);
        if (state.modeState.mode === 'target_blast' && !shotCounted) {
          shotCounted = true;
          state.modeState.shotsFired++;
          checkTargetHits(state, proj.x, proj.y, proj.weapon.blastRadius, fx);
        }
        state.projectiles.splice(pi, 1);
        continue;
      } else if (tickResult === 'consume') {
        state.projectiles.splice(pi, 1);
        continue;
      } else if (tickResult && typeof tickResult === 'object' && tickResult.consume) {
        state.deferredSpawns.push(...tickResult.spawns);
        state.projectiles.splice(pi, 1);
        continue;
      }
    }

    // Physics step
    const result = updateSingleProjectile(proj, state);

    if ((result.type === 'hit_terrain' || result.type === 'hit_tank' || result.type === 'hit_target') && result.hitX !== undefined && result.hitY !== undefined) {
      const x = result.hitX;
      const y = result.hitY;

      // Bouncing projectile + tank hit — explode but keep flying
      if (result.type === 'hit_tank' && proj.bouncesRemaining > 0) {
        proj.bouncesRemaining--;
        const { knockedPlayers, hitPlayers, damages: bounceDmgs } = defaultExplosion(proj, x, y, state, fx);
        for (let hi = 0; hi < hitPlayers.length; hi++) {
          recordTrickHit(state, proj, hitPlayers[hi], x, y, bounceDmgs[hi] ?? 0, true, fx);
        }
        state.firingPhaseKnockedPlayers.push(...knockedPlayers);
        continue; // projectile survives — keep flying
      }

      // Call weapon.onImpact() for custom spawns
      let spawns: DeferredSpawn[] = [];
      if (proj.weapon.onImpact) {
        spawns = proj.weapon.onImpact(proj, x, y, result.type, result.hitTankIndex ?? -1, state, fx);
      }

      // Default explosion (crater + damage + knockback + effects)
      const { knockedPlayers, hitPlayers: impactHitPlayers, damages: impactDmgs } = defaultExplosion(proj, x, y, state, fx);
      for (let hi = 0; hi < impactHitPlayers.length; hi++) {
        recordTrickHit(state, proj, impactHitPlayers[hi], x, y, impactDmgs[hi] ?? 0, result.type === 'hit_tank', fx);
      }
      state.firingPhaseKnockedPlayers.push(...knockedPlayers);

      // Schedule spawned children
      if (spawns.length > 0) {
        state.deferredSpawns.push(...spawns);
      }

      // Target Blast target check
      if (state.modeState.mode === 'target_blast' && !shotCounted) {
        shotCounted = true;
        state.modeState.shotsFired++;
        checkTargetHits(state, x, y, proj.weapon.blastRadius, fx);
      }

      state.projectiles.splice(pi, 1);
    } else if (result.type === 'bounced' && result.hitX !== undefined && result.hitY !== undefined) {
      if (proj.weapon.onBounce) {
        // Custom bounce behavior — replaces defaultExplosion at bounce point
        const bounceResult = proj.weapon.onBounce(proj, result.hitX, result.hitY, state, fx);
        if (bounceResult.spawns.length > 0) state.deferredSpawns.push(...bounceResult.spawns);
        if (bounceResult.knockedPlayers && bounceResult.knockedPlayers.length > 0) {
          state.firingPhaseKnockedPlayers.push(...bounceResult.knockedPlayers);
        }
        // Trick recording for custom bounce hits
        if (bounceResult.hitPlayers && bounceResult.hitPlayers.length > 0) {
          const bncDmgs = bounceResult.damages ?? [];
          for (let hi = 0; hi < bounceResult.hitPlayers.length; hi++) {
            recordTrickHit(state, proj, bounceResult.hitPlayers[hi], result.hitX!, result.hitY!, bncDmgs[hi] ?? 0, false, fx);
          }
        }
      } else {
        // Default bounce explosion — crater + damage + effects but projectile keeps flying
        const { knockedPlayers, hitPlayers: bounceHits, damages: bncDmgs } = defaultExplosion(proj, result.hitX, result.hitY, state, fx);
        for (let hi = 0; hi < bounceHits.length; hi++) {
          recordTrickHit(state, proj, bounceHits[hi], result.hitX!, result.hitY!, bncDmgs[hi] ?? 0, false, fx);
        }
        state.firingPhaseKnockedPlayers.push(...knockedPlayers);
      }
    } else if (result.type === 'hit_void' && result.hitX !== undefined && result.hitY !== undefined) {
      if (state.modeState.mode === 'target_blast' && !shotCounted) {
        shotCounted = true;
        state.modeState.shotsFired++;
      }
      const consumeX = result.hitX;
      const consumeY = result.hitY;
      const firedBy = proj.owner;

      // Per-consume VFX (particles, shake, flash, distortion, freeze) — these feel good per-hit
      const voidColor = getActiveTheme(state.stageId).voidEdgeColor;
      fx.push({ type: 'particles', method: 'spawnVoidProjectileConsume', args: [consumeX, consumeY, voidColor] });
      fx.push({ type: 'camera_shake', intensity: VOID_CONSUME.CAMERA_SHAKE, decay: 0.85 });
      fx.push({ type: 'screen_shake', intensity: VOID_CONSUME.SCREEN_SHAKE, decay: 0.85 });
      fx.push({ type: 'screen_flash', color: 0xff00ff, alpha: VOID_CONSUME.FLASH_ALPHA });
      fx.push({ type: 'void_edge_distortion', x: consumeX, y: consumeY });
      state.freezeUntil = Math.max(state.freezeUntil, state.frameCount + msToFrames(VOID_CONSUME.FREEZE_MS));

      // Accumulate meal (skip void's own orbs — firedBy === -1)
      if (firedBy !== -1) {
        const prevCount = state.voidMealCount;
        state.voidMealCount++;
        // Weighted average X for retaliation aim point
        state.voidMealX = (state.voidMealX * prevCount + consumeX) / state.voidMealCount;

        // Mood gain
        state.voidMood = Math.min(100, state.voidMood + VOID_PERSONALITY.MOOD_PER_CONSUME);
        fx.push({ type: 'void_mood_update', mood: state.voidMood });

        // Personality text (mood-driven chance)
        const line = rollConsumeLine(state.voidMood);
        if (line) {
          fx.push({
            type: 'announce_world',
            text: line,
            x: consumeX,
            y: state.voidY - 30,
            color: VOID_PERSONALITY.TEXT_COLOR,
            fontSize: getMoodFontSize(state.voidMood),
          });
        }
      }

      state.projectiles.splice(pi, 1);
    } else if (result.type === 'out_of_bounds') {
      if (state.modeState.mode === 'target_blast' && !shotCounted) {
        shotCounted = true;
        state.modeState.shotsFired++;
      }
      state.projectiles.splice(pi, 1);
    }
  }

  // --- Batched meal evaluation: roll retaliation ONCE for the whole firing phase ---
  if (state.voidMealCount > 0 && state.projectiles.length === 0 && state.deferredSpawns.length === 0 && !state.voidRetaliationActive) {
    const mealSize = state.voidMealCount;
    const mealX = state.voidMealX;

    // Chance scales with meal size
    const chance = Math.min(
      VOID_PERSONALITY.RETALIATION_CHANCE_BASE + (mealSize - 1) * VOID_PERSONALITY.RETALIATION_CHANCE_SCALE,
      VOID_PERSONALITY.RETALIATION_CHANCE_CAP,
    );

    if (randomChance(chance)) {
      // Orb count scales with meal size
      const orbCount = Math.min(
        VOID_PERSONALITY.RETALIATION_ORB_BASE + Math.floor((mealSize - 1) * VOID_PERSONALITY.RETALIATION_ORB_SCALE),
        VOID_PERSONALITY.RETALIATION_ORB_CAP,
      );

      const baseDelay = msToFrames(randomRange(VOID_CONSUME.DELAY_MIN, VOID_CONSUME.DELAY_MAX));
      const foreshadowFrames = msToFrames(VOID_CONSUME.FORESHADOW_MS);
      const launchFrame = state.frameCount + baseDelay;

      // All orbs share the same launchTime (simultaneous fan launch)
      for (let i = 0; i < orbCount; i++) {
        state.voidRetaliationQueue.push({
          x: mealX + (random() - 0.5) * VOID_CONSUME.SPREAD_X * 2,
          launchTime: launchFrame,
          foreshadowTime: launchFrame - foreshadowFrames,
          launched: false,
          foreshadowed: false,
        });
      }
      state.voidRetaliationActive = true;
    }

    // Reset meal counters — these are transient accumulators only live during firing phase.
    // Always 0 at turn-boundary hash checkpoints, so intentionally excluded from hash.
    state.voidMealCount = 0;
    state.voidMealX = 0;
  }

  // --- Exit firing when all projectiles done AND no deferred spawns pending ---
  if (state.projectiles.length === 0 && state.deferredSpawns.length === 0 && !state.voidRetaliationActive && !state.beamActive) {
    const knockedPlayers = state.firingPhaseKnockedPlayers;
    state.firingPhaseKnockedPlayers = [];
    resetSubExplosionTracking();

    // Deduplicate knocked players
    const uniqueKnocked = [...new Set(knockedPlayers)];

    // armedDisrupted VFX — show "DISRUPTED!" for any player whose armed shot was knocked away
    for (let i = 0; i < state.players.length; i++) {
      const p = state.players[i];
      if (p.armedDisrupted) {
        p.armedDisrupted = false;
        fx.push({ type: 'announce_world', text: 'DISRUPTED!', x: p.x, y: p.y - 60, color: 0xff3333, fontSize: 32, hold: 0.8 });
        fx.push({ type: 'screen_shake', intensity: 5, decay: 0.85 });
      }
    }

    // Void retaliation can hit the current player — apply knockback directly
    // (DI/edgeguard/defense assume currentPlayer is the attacker, not a knocked defender)
    const selfIdx = uniqueKnocked.indexOf(state.currentPlayer);
    if (selfIdx !== -1) {
      uniqueKnocked.splice(selfIdx, 1);
      const p = state.players[state.currentPlayer];
      if (p.alive) {
        p.vx = p.diPendingVx;
        p.vy = p.diPendingVy;
        p.diPendingVx = 0;
        p.diPendingVy = 0;
        p.diAngleShift = 0;
        p.kbStackHitCount = 0;
        p.kbStackFirstFrame = 0;
        enterTumble(p, {
          hitstunPerSpeed: TUMBLE.HITSTUN_PER_SPEED,
          minHitstun: TUMBLE.MIN_HITSTUN,
          maxHitstun: TUMBLE.MAX_HITSTUN,
          speedGated: true,
          damagePercent: p.damagePercent,
        });
        if (p.tumble) {
          mirrorEnterTumble(p, 'knockback');
        } else if (p.actionState.tag === 'di_frozen') {
          mirrorAirborne(p);
        }
        p.settled = false;
        p.grounded = false;
      }
    }

    // Classic mode: skip DI/edgeguard/defense — check HP deaths, then straight to awaiting
    if (modeIsClassic(state.modeState.mode)) {
      // Check for HP deaths (pendingDeath was set in explosion.ts)
      const deadPlayers = state.players.filter(p => p.pendingDeath && p.alive);
      if (deadPlayers.length > 0) {
        transitionPhase(state, 'death_sequence', fx);
        state.turnTimer = TURN_FLOW.TURN_DELAY;
      } else {
        // Apply knockback directly (no DI window) — knockback is already on diPendingVx/Vy
        for (const ki of uniqueKnocked) {
          const p = state.players[ki];
          if (!p.alive) continue;
          p.vx = p.diPendingVx;
          p.vy = p.diPendingVy;
          p.diPendingVx = 0;
          p.diPendingVy = 0;
          p.diAngleShift = 0;
          p.kbStackHitCount = 0;
          p.kbStackFirstFrame = 0;
          enterTumble(p, {
            hitstunPerSpeed: TUMBLE.HITSTUN_PER_SPEED,
            minHitstun: TUMBLE.MIN_HITSTUN,
            maxHitstun: TUMBLE.MAX_HITSTUN,
            speedGated: false,
            damagePercent: p.damagePercent,
          });
          mirrorEnterTumble(p, 'knockback');
          p.settled = false;
          p.grounded = false;
        }
        transitionPhase(state, 'awaiting_next_turn', fx);
        state.turnTimer = TURN_FLOW.TURN_DELAY;
      }
    } else if (uniqueKnocked.length > 0) {
      // Snapshot BEFORE startDIWindow — preserves original diPending values
      // (startDIWindow may call applyDeferredKnockback which clears them)
      const firingResolvePlayers = uniqueKnocked.map(ki => {
        const p = state.players[ki];
        return {
          playerIndex: ki,
          x: p.x, y: p.y,
          diPendingVx: p.diPendingVx,
          diPendingVy: p.diPendingVy,
          kbStackHitCount: p.kbStackHitCount,
          damagePercent: p.damagePercent,
          weaponCharge: p.weaponCharge,
        };
      });

      // Call startDIWindow (may mutate state via applyDeferredKnockback for sub-threshold players)
      const diOpened = startDIWindow(state, uniqueKnocked);

      // Emit with authoritative DI decision so both clients agree
      fx.push({ type: 'firing_resolved', players: firingResolvePlayers, diOpened });

      if (diOpened) {
        transitionPhase(state, 'di_window', fx);
        fx.push({ type: 'di_show' });
        fx.push({ type: 'announce_screen', text: 'DI!', color: 0x00ffff, fontSize: 56, hold: 0.3 });
        fx.push({ type: 'screen_flash', color: DI.ENTRY_FLASH_COLOR, alpha: DI.ENTRY_FLASH_ALPHA });
        const diPlayer = state.players[state.diActivePlayer];
        fx.push({ type: 'particles', method: 'spawnDIEntry', args: [diPlayer.x, diPlayer.y, diPlayer.color] });
        fx.push({ type: 'camera_follow', x: diPlayer.x, y: diPlayer.y });
      } else {
        transitionPhase(state, 'awaiting_next_turn', fx);
        state.turnTimer = TURN_FLOW.TURN_DELAY;
      }
    } else {
      transitionPhase(state, 'awaiting_next_turn', fx);
      state.turnTimer = TURN_FLOW.TURN_DELAY;
    }
  }

  // Render all projectiles
  fx.push({ type: 'render_projectiles', projectiles: state.projectiles, wind: state.wind });
}
